/*
 * /appointment (list page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	cn,
	Container,
	Page,
	PageHeader,
	Placeholder,
	ScrollArea,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@emr/react-ui"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
	CalendarDays,
	ChevronLeft,
	ChevronRight,
	Clock,
	ExternalLink,
	Stethoscope,
	User,
	Video,
} from "lucide-react"
import { useState, useMemo } from "react"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/appointment/")({
	component: AppointmentsPage,
	loader: rolePageLoader(["practitioner", "nurse", "admin", "super_admin"]),
})

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function toDateStr(d: Date) {
	return d.toISOString().slice(0, 10)
}

function formatTime(d: Date | string, timezone?: string) {
	return new Date(d).toLocaleTimeString("en-CA", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZone: timezone ?? undefined,
	})
}

function formatDate(d: Date) {
	return d.toLocaleDateString("en-CA", {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
	})
}

function statusVariant(
	status: string | null,
): "default" | "secondary" | "destructive" | "outline" {
	switch (status) {
		case "scheduled":
			return "default"
		case "completed":
			return "secondary"
		case "cancelled":
			return "destructive"
		default:
			return "outline"
	}
}

/* ─── Page ────────────────────────────────────────────────────────────────── */

function AppointmentsPage() {
	const [selectedDate, setSelectedDate] = useState<Date>(() => {
		const d = new Date()
		d.setHours(0, 0, 0, 0)
		return d
	})
	const [practitionerFilter, setPractitionerFilter] = useState<string>("all")
	const [hideCancelled, setHideCancelled] = useState(false)
	const [selectedApptId, setSelectedApptId] = useState<string | null>(null)

	const dateFrom = new Date(selectedDate)
	dateFrom.setHours(0, 0, 0, 0)
	const dateTo = new Date(selectedDate)
	dateTo.setHours(23, 59, 59, 999)

	const { data: practitioners } = trpc.practitioner.getAllByFilter.useQuery({
		where: { active: true },
	})

	const { data: appointments, isLoading } =
		trpc.appointment.getByDateRange.useQuery({
			dateFrom: dateFrom.toISOString(),
			dateTo: dateTo.toISOString(),
			practitionerId:
				practitionerFilter !== "all" ? practitionerFilter : undefined,
		})

	const visibleAppointments = useMemo(
		() =>
			hideCancelled
				? (appointments?.filter(
						(a) => a.active && a.status !== "cancelled",
					) ?? [])
				: (appointments ?? []),
		[appointments, hideCancelled],
	)

	const selectedAppt =
		visibleAppointments.find((a) => a.id === selectedApptId) ?? null

	function prevDay() {
		setSelectedDate((d) => {
			const next = new Date(d)
			next.setDate(next.getDate() - 1)
			return next
		})
		setSelectedApptId(null)
	}

	function nextDay() {
		setSelectedDate((d) => {
			const next = new Date(d)
			next.setDate(next.getDate() + 1)
			return next
		})
		setSelectedApptId(null)
	}

	function goToday() {
		const d = new Date()
		d.setHours(0, 0, 0, 0)
		setSelectedDate(d)
		setSelectedApptId(null)
	}

	const isToday = toDateStr(selectedDate) === toDateStr(new Date())

	return (
		<Page>
			<Container>
				<PageHeader title="Appointments" />

				{/* ─── Controls ─────────────────────────────────────────────── */}
				<div className="flex flex-wrap items-center gap-3 mb-4">
					{/* Date navigation */}
					<div className="flex items-center gap-2">
						<Button variant="outline" size="icon" onClick={prevDay}>
							<ChevronLeft className="w-4 h-4" />
						</Button>
						<button
							onClick={goToday}
							className={cn(
								"px-3 py-1.5 rounded-md text-sm font-medium transition-colors min-w-[200px] text-center",
								isToday
									? "bg-primary text-primary-foreground"
									: "bg-muted hover:bg-muted/80",
							)}
						>
							{formatDate(selectedDate)}
						</button>
						<Button variant="outline" size="icon" onClick={nextDay}>
							<ChevronRight className="w-4 h-4" />
						</Button>
					</div>

					{/* Hide cancelled toggle */}
					<Button
						variant={hideCancelled ? "default" : "outline"}
						size="sm"
						onClick={() => {
							setHideCancelled((v) => !v)
							setSelectedApptId(null)
						}}
					>
						{hideCancelled ? "Showing active only" : "Show active only"}
					</Button>

					{/* Practitioner filter */}
					<Select
						value={practitionerFilter}
						onValueChange={(v) => {
							setPractitionerFilter(v)
							setSelectedApptId(null)
						}}
					>
						<SelectTrigger className="w-52">
							<SelectValue placeholder="All practitioners" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All practitioners</SelectItem>
							{practitioners?.map((p) => (
								<SelectItem key={p.id} value={p.id}>
									{p.firstName} {p.lastName}
									{p.professionalDesignation
										? `, ${p.professionalDesignation}`
										: ""}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* ─── Split panel ──────────────────────────────────────────── */}
				<div className="flex gap-4" style={{ height: "calc(100vh - 240px)" }}>
					{/* Left: list */}
					<div className="w-80 shrink-0 flex flex-col gap-2">
						{isLoading ? (
							<Placeholder title="Loading appointments..." />
						) : visibleAppointments.length === 0 ? (
							<Placeholder
								title={
									appointments && appointments.length > 0
										? "All appointments are cancelled."
										: "No appointments for this day."
								}
							/>
						) : (
							<ScrollArea className="flex-1">
								<div className="flex flex-col gap-2 pr-2">
									{visibleAppointments.map((appt) => (
										<AppointmentCard
											key={appt.id}
											appt={appt}
											selected={appt.id === selectedApptId}
											onClick={() =>
												setSelectedApptId(
													appt.id === selectedApptId ? null : appt.id,
												)
											}
										/>
									))}
								</div>
							</ScrollArea>
						)}
					</div>

					{/* Right: detail */}
					<div className="flex-1 min-w-0">
						{selectedAppt ? (
							<AppointmentDetail appt={selectedAppt} />
						) : (
							<div className="h-full flex items-center justify-center text-muted-foreground text-sm">
								Select an appointment to view details
							</div>
						)}
					</div>
				</div>
			</Container>
		</Page>
	)
}

/* ─── Appointment card (list item) ────────────────────────────────────────── */

type ApptRecord = {
	id: string
	startsAt: Date | string
	endsAt: Date | string
	timezone: string
	type: string | null
	status: string
	active: boolean
	reason: string | null
	notes: string | null
	patientId: string | null
	practitionerId: string
	practitionerFirstName: string | null
	practitionerLastName: string | null
	practitionerDesignation: string | null
	patientFirstName: string | null
	patientLastName: string | null
}

function AppointmentCard({
	appt,
	selected,
	onClick,
}: {
	appt: ApptRecord
	selected: boolean
	onClick: () => void
}) {
	const start = formatTime(appt.startsAt, appt.timezone)
	const end = formatTime(appt.endsAt, appt.timezone)
	const patientName =
		appt.patientFirstName && appt.patientLastName
			? `${appt.patientFirstName} ${appt.patientLastName}`
			: "Unknown patient"
	const practName =
		appt.practitionerFirstName && appt.practitionerLastName
			? `${appt.practitionerFirstName} ${appt.practitionerLastName}`
			: "Unknown"

	const isDimmed = appt.status === "cancelled" || !appt.active

	return (
		<Card
			className={cn(
				"cursor-pointer transition-colors border",
				isDimmed
					? "border-destructive/40 bg-destructive/5 opacity-70"
					: selected
						? "ring-2 ring-primary bg-accent/30"
						: "hover:bg-accent/20",
				selected && isDimmed && "ring-2 ring-destructive/50 opacity-100",
			)}
			onClick={onClick}
		>
			<CardContent className="py-3 px-4 space-y-1.5">
				<div className="flex items-center justify-between gap-2">
					<p
						className={cn(
							"font-medium text-sm truncate",
							isDimmed && "line-through text-muted-foreground",
						)}
					>
						{patientName}
					</p>
					<Badge
						variant={!appt.active ? "destructive" : statusVariant(appt.status)}
						className="shrink-0 text-xs"
					>
						{!appt.active ? "inactive" : appt.status}
					</Badge>
				</div>
				<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<Clock className="w-3 h-3 shrink-0" />
					<span>
						{start} – {end}
					</span>
				</div>
				<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<Stethoscope className="w-3 h-3 shrink-0" />
					<span className="truncate">{practName}</span>
				</div>
				{appt.type && (
					<p className="text-xs text-muted-foreground truncate">{appt.type}</p>
				)}
			</CardContent>
		</Card>
	)
}

/* ─── Appointment detail panel ─────────────────────────────────────────────── */

function AppointmentDetail({ appt }: { appt: ApptRecord }) {
	const start = formatTime(appt.startsAt, appt.timezone)
	const end = formatTime(appt.endsAt, appt.timezone)
	const patientName =
		appt.patientFirstName && appt.patientLastName
			? `${appt.patientFirstName} ${appt.patientLastName}`
			: "Unknown patient"
	const practName =
		appt.practitionerFirstName && appt.practitionerLastName
			? `${appt.practitionerFirstName} ${appt.practitionerLastName}`
			: "Unknown"
	const practDisplay = appt.practitionerDesignation
		? `${practName}, ${appt.practitionerDesignation}`
		: practName

	return (
		<Card className="h-full">
			<CardContent className="p-6 space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between gap-4">
					<div>
						<h2 className="text-xl font-semibold">{patientName}</h2>
						<p className="text-sm text-muted-foreground mt-0.5">{practDisplay}</p>
					</div>
					<Badge variant={statusVariant(appt.status)}>{appt.status}</Badge>
				</div>

				{/* Time */}
				<div className="flex items-center gap-2 text-sm">
					<CalendarDays className="w-4 h-4 text-muted-foreground" />
					<span>
						{start} – {end}{" "}
						<span className="text-muted-foreground">({appt.timezone})</span>
					</span>
				</div>

				{/* Type / reason */}
				{(appt.type || appt.reason) && (
					<div className="space-y-1">
						{appt.type && (
							<div className="text-sm">
								<span className="font-medium">Type: </span>
								{appt.type}
							</div>
						)}
						{appt.reason && (
							<div className="text-sm">
								<span className="font-medium">Reason: </span>
								{appt.reason}
							</div>
						)}
					</div>
				)}

				{/* Notes */}
				{appt.notes && (
					<div className="space-y-1">
						<p className="text-sm font-medium">Notes</p>
						<p className="text-sm text-muted-foreground whitespace-pre-wrap rounded-md bg-muted px-3 py-2">
							{appt.notes}
						</p>
					</div>
				)}

				{/* Actions */}
				<div className="flex flex-wrap gap-3 pt-2">
					{appt.patientId && (
						<Link
							to="/appointment/$patientId"
							params={{ patientId: appt.patientId }}
						>
							<Button variant="outline" className="gap-2">
								<User className="w-4 h-4" />
								View Patient
								<ExternalLink className="w-3 h-3" />
							</Button>
						</Link>
					)}
					{/* Video call — placeholder for future integration */}
					<Button variant="outline" className="gap-2" disabled>
						<Video className="w-4 h-4" />
						Start Video Call
					</Button>
				</div>
			</CardContent>
		</Card>
	)
}
